import React from "react";
import {
  FormControl,
  FormGroup,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import { Checkbox } from "./Checkbox";
import { HelpOutlineOutlined } from "@mui/icons-material";
import { normalizeOption } from "./normalizeOption";

export interface ICheckboxOption {
  label: string;
  value?: string;
  helperText?: string;
  tooltipTitle?: React.ReactNode;
  disabled?: boolean;
}

export interface ICheckboxGroupProps {
  label: string;
  options: (string | ICheckboxOption)[];
  values: string[];
  direction?: "row" | "column";
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  helperText?: string;
  tooltipTitle?: React.ReactNode;
}

export const CheckboxGroup: React.FC<ICheckboxGroupProps> = ({
  label,
  options,
  onChange,
  values,
  direction = "column",
  helperText,
  tooltipTitle,
}) => {
  return (
    <FormControl>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle2" color="text.primary">
          {label}
        </Typography>
        {tooltipTitle && (
          <Tooltip title={tooltipTitle} arrow>
            <HelpOutlineOutlined
              sx={{
                height: "16px",
                width: "16px",
                color: "secondary.main",
              }}
            />
          </Tooltip>
        )}
      </Stack>
      <FormGroup
        sx={{
          flexDirection: direction,
          gap: direction === "row" ? 2 : 0,
        }}
      >
        {options.map((option, index) => {
          const normalizedOption = normalizeOption(option);
          return (
            <Checkbox
              key={index}
              label={normalizedOption.label}
              value={normalizedOption.value!}
              checked={values.includes(normalizedOption.value!)}
              onChange={onChange}
              helperText={normalizedOption.helperText}
              tooltipTitle={normalizedOption.tooltipTitle}
              disabled={normalizedOption.disabled}
            />
          );
        })}
      </FormGroup>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};
